import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

export interface EmailActivityListItem {
  email_tracking_id: string;
  email_type: string;
  recipient_email: string;
  recipient_emails?: string[] | null;
  subject: string;
  created_at: string;
}

export interface EmailActivityListResponse {
  items: EmailActivityListItem[];
  email_types: string[];
  total: number;
  current_page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface EmailActivityFilters {
  page?: number;
  page_size?: number;
  recipient?: string;
  email_type?: string;
  from_date?: string;
  to_date?: string;
}

export interface EmailTemplatePreviewItem {
  email_type: string;
  name: string;
  subject: string;
  html: string;
  purpose?: string | null;
}

export interface EmailTemplatePreviewResponse {
  templates: EmailTemplatePreviewItem[];
  total: number;
}

interface EmailActivityState {
  listLoading: boolean;
  listError: string | null;
  items: EmailActivityListItem[];
  emailTypes: string[];
  total: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
  templatesLoading: boolean;
  templatesError: string | null;
  templates: EmailTemplatePreviewItem[];
  templatesFetched: boolean;
}

const initialState: EmailActivityState = {
  listLoading: false,
  listError: null,
  items: [],
  emailTypes: [],
  total: 0,
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
  templatesLoading: false,
  templatesError: null,
  templates: [],
  templatesFetched: false,
};

export const fetchEmailActivity = createAsyncThunk(
  "emailActivity/fetchList",
  async (params: EmailActivityFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchEmailActivity(params));
      return (response?.content ?? response) as EmailActivityListResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.response?.message ||
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to fetch email activity"
      );
    }
  }
);

export const fetchEmailActivityTemplates = createAsyncThunk(
  "emailActivity/fetchTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchEmailActivityTemplates());
      return (response?.content ?? response) as EmailTemplatePreviewResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.response?.message ||
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to fetch email templates"
      );
    }
  }
);

const emailActivitySlice = createSlice({
  name: "emailActivity",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchEmailActivity.pending, state => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchEmailActivity.fulfilled, (state, action) => {
        state.listLoading = false;
        state.items = action.payload.items ?? [];
        state.emailTypes = action.payload.email_types ?? [];
        state.total = action.payload.total ?? 0;
        state.currentPage = action.payload.current_page ?? 1;
        state.pageSize = action.payload.page_size ?? 25;
        state.totalPages = action.payload.total_pages ?? 0;
        state.hasNext = action.payload.has_next ?? false;
        state.hasPrevious = action.payload.has_previous ?? false;
      })
      .addCase(fetchEmailActivity.rejected, (state, action) => {
        state.listLoading = false;
        state.listError =
          (action.payload as string) || "Failed to fetch email activity";
        toast.error(state.listError);
      })
      .addCase(fetchEmailActivityTemplates.pending, state => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchEmailActivityTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templatesFetched = true;
        state.templates = action.payload.templates ?? [];
      })
      .addCase(fetchEmailActivityTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesFetched = true;
        state.templatesError =
          (action.payload as string) || "Failed to fetch email templates";
        toast.error(state.templatesError);
      });
  },
});

export default emailActivitySlice.reducer;
